/**
 * sw-register.js
 * Registro del Service Worker y gestión de actualizaciones.
 *
 * Flujo de actualización:
 *  1. El SW nuevo se activa automáticamente (skipWaiting).
 *  2. Si el panel de edición está abierto, se muestra un banner
 *     manual para que el admin decida cuándo recargar.
 *  3. En cualquier otro caso, la página recarga sola y muestra
 *     un banner informativo de 3 segundos post-recarga.
 *  4. Al recuperar el foco de la pestaña se comprueba si hay
 *     una versión nueva disponible.
 */

export function registrarServiceWorker() {
  if (import.meta.env.DEV) {
    console.info('[SW] Modo desarrollo — Service Worker desactivado.');
    return;
  }

  if (!('serviceWorker' in navigator)) {
    console.warn('[SW] Service Workers no soportados en este navegador.');
    return;
  }

  // Si venimos de una actualización automática, mostrar banner informativo
  if (sessionStorage.getItem('sw-actualizado')) {
    sessionStorage.removeItem('sw-actualizado');
    mostrarBanner({ mensaje: '✨ App actualizada a la última versión' });
  }

  let recargando = false;

  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (recargando) return;
    recargando = true;

    const panelEdicionAbierto = document
      .getElementById('panel-edicion')
      ?.classList.contains('edicion-panel--visible');

    if (panelEdicionAbierto) {
      mostrarBanner({
        mensaje: '✨ Nueva versión disponible',
        boton: 'Actualizar',
        onClick: () => {
          sessionStorage.setItem('sw-actualizado', 'true');
          window.location.reload();
        },
      });
      return;
    }

    sessionStorage.setItem('sw-actualizado', 'true');
    window.location.reload();
  });

  // Comprobar actualización al volver a la pestaña
  window.addEventListener('load', async () => {
    try {
      const registro = await navigator.serviceWorker.getRegistration();
      if (!registro) return;

      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') {
          registro.update();
        }
      });
    } catch (error) {
      console.error('[SW] Error:', error);
    }
  });
}

// ── Toast de actualización ────────────────────────────

/**
 * Muestra un toast en la parte inferior de la pantalla.
 * Si se pasa `boton`, el toast espera interacción del usuario.
 * Si no, desaparece solo a los 3 segundos.
 *
 * @param {object}   opciones
 * @param {string}   opciones.mensaje  - Texto del toast
 * @param {string}   [opciones.boton]  - Texto del botón (opcional)
 * @param {Function} [opciones.onClick] - Acción del botón (opcional)
 */
function mostrarBanner({ mensaje, boton, onClick }) {
  if (document.getElementById('sw-update-toast')) return;

  const aviso = document.createElement('div');
  aviso.id = 'sw-update-toast';
  aviso.innerHTML = boton
    ? `<span>${mensaje}</span><button type="button" id="sw-reload-btn">${boton}</button>`
    : `<span>${mensaje}</span>`;

  Object.assign(aviso.style, {
    position: 'fixed',
    bottom: '1.5rem',
    left: '50%',
    transform: 'translateX(-50%)',
    background: '#C8762A',
    color: '#fff',
    padding: '0.75rem 1.25rem',
    borderRadius: '2rem',
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
    zIndex: '9999',
    boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
    fontSize: '0.9rem',
    whiteSpace: 'nowrap',
  });

  document.body.appendChild(aviso);

  if (boton && onClick) {
    document.getElementById('sw-reload-btn').addEventListener('click', () => {
      aviso.remove();
      onClick();
    });
  } else {
    setTimeout(() => aviso.remove(), 3000);
  }
}
